import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { imgUrl, imgSrcSet } from "@/lib/img";

type Result = {
  slug: string;
  title: string;
  excerpt?: string | null;
  category?: string | null;
  thumbnail?: string | null;
};

export function HeaderSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Result[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      const like = `%${term.replace(/[%_]/g, "\\$&")}%`;
      const { data, error } = await (supabase as any)
        .from("articles_public")
        .select("slug,title,excerpt,category,thumbnail")
        .or(`title.ilike.${like},excerpt.ilike.${like},category.ilike.${like}`)
        .limit(8);
      if (cancelled) return;
      setResults(error ? [] : ((data ?? []) as Result[]));
      setActive(0);
      setLoading(false);
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const go = (slug: string) => {
    setOpen(false);
    setQ("");
    navigate(`/artigo/${slug}`);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, Math.max(0, results.length - 1))); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (results[active]) go(results[active].slug);
      else if (q.trim().length >= 2) { setOpen(false); navigate(`/blog?q=${encodeURIComponent(q.trim())}`); }
    }
  };

  return (
    <div ref={rootRef} className="relative">
      {/* Trigger — mantém o espaço no menu quando fechado */}
      <button
        type="button"
        aria-label="Buscar no blog"
        onClick={() => setOpen(true)}
        className={cn(
          "inline-flex items-center justify-center w-9 h-9 rounded-md text-foreground/70 hover:text-primary hover:bg-muted transition-colors",
          open && "opacity-0 pointer-events-none",
        )}
      >
        <Search className="w-4 h-4" />
      </button>

      {/* Overlay expansível — não ultrapassa o item "Blog" à esquerda */}
      <div
        className={cn(
          "absolute top-1/2 -translate-y-1/2 right-0 z-40 overflow-visible",
          "transition-[width,opacity] duration-700 ease-in-out",
          open ? "w-[520px] max-w-[60vw] opacity-100" : "w-9 opacity-0 pointer-events-none",
        )}
        aria-hidden={!open}
      >
        <div
          className={cn(
            "flex items-center gap-2 bg-background border rounded-md h-10 pl-1 pr-2 shadow-card w-full",
            "transition-colors duration-500",
            open ? "border-foreground/15 focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/15" : "border-transparent",
          )}
        >
          <button
            type="button"
            onClick={() => {
              const term = q.trim();
              if (term.length >= 2) { setOpen(false); navigate(`/blog?q=${encodeURIComponent(term)}`); }
              else inputRef.current?.focus();
            }}
            aria-label="Executar busca"
            tabIndex={open ? 0 : -1}
            className="shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-md text-foreground/60 hover:text-primary hover:bg-muted transition-colors"
          >
            <Search className="w-4 h-4" />
          </button>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Buscar no blog…"
            tabIndex={open ? 0 : -1}
            className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground min-w-0"
          />
          {loading && <Loader2 className="w-4 h-4 text-foreground/60 animate-spin shrink-0" />}
          <button
            type="button"
            onClick={() => { setQ(""); setOpen(false); }}
            aria-label="Fechar busca"
            tabIndex={open ? 0 : -1}
            className="shrink-0 w-6 h-6 inline-flex items-center justify-center rounded hover:bg-muted text-foreground/65"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {open && q.trim().length >= 2 && (
          <div className="absolute left-0 right-0 top-full mt-2 rounded-md border border-border bg-popover shadow-floating overflow-hidden animate-fade-in">
            {loading && !results.length ? (
              <div className="px-3 py-3 text-xs text-muted-foreground">Buscando…</div>
            ) : results.length === 0 ? (
              <div className="px-3 py-3 text-xs text-muted-foreground">Nenhum resultado para "{q}".</div>
            ) : (
              <ul className="max-h-[420px] overflow-auto py-1">
                {results.map((r, i) => (
                  <li key={r.slug}>
                    <button
                      type="button"
                      onMouseEnter={() => setActive(i)}
                      onClick={() => go(r.slug)}
                      className={cn(
                        "w-full flex items-start gap-3 px-3 py-2 text-left",
                        i === active ? "bg-muted" : "hover:bg-muted/60",
                      )}
                    >
                      {r.thumbnail ? (
                        <img src={imgUrl(r.thumbnail, 80)} alt={r.title || ""} loading="lazy" decoding="async" width={40} height={40} className="w-10 h-10 rounded object-cover shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded bg-primary/10 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        {r.category && (
                          <div className="text-[10px] uppercase tracking-widest text-primary font-semibold truncate">
                            {r.category}
                          </div>
                        )}
                        <div className="text-sm font-medium text-foreground truncate">{r.title}</div>
                        {r.excerpt && (
                          <div className="text-[11px] text-muted-foreground line-clamp-2">{r.excerpt}</div>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    type="button"
                    onClick={() => { setOpen(false); navigate(`/blog?q=${encodeURIComponent(q.trim())}`); }}
                    className="w-full text-center px-3 py-2 text-xs text-primary hover:bg-muted border-t border-border"
                  >
                    Ver todos os resultados para "{q}"
                  </button>
                </li>
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default HeaderSearch;
